/**
 * DiagramsSidebar Component
 *
 * A collapsible left sidebar for the Diagram_Browser listing "All diagrams",
 * "Unfiled", and one entry per Diagram_Folder in use, each with its diagram
 * count. Exactly one entry is highlighted at a time; the selection is owned by
 * the parent and reflected in the URL (`?folder=`). See Requirement 9.1.
 *
 * Mirrors the layout and button styling of ReportsSidebar, using the generic
 * SecondarySideBar for the open/close toggle, layout, and collapsed icon rail.
 */

import { Folder, FolderOpen, Layers } from "lucide-react"
import { cn } from "@/lib/utils"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import SecondarySideBar from "@/components/shared/SecondarySideBar"
import { FOLDER_ALL, type FolderEntry } from "@/components/diagrams/diagramBrowser"

/** Title shown in the sidebar header and the collapsed "Expand" button label. */
const SIDEBAR_TITLE: string = "Diagrams"

/** Props for the DiagramsSidebar component. */
interface DiagramsSidebarProps {
  /** The folder entries to list (All diagrams, Unfiled, then real folders). */
  folders: FolderEntry[]
  /** The currently selected folder value (highlighted). */
  selectedFolder: string
  /** Called with the folder value the user clicked. */
  onSelectFolder: (folder: string) => void
}

/**
 * Pick the icon for a folder entry: a layers icon for "All diagrams", a folder
 * icon for everything else (the selected entry uses the open-folder variant).
 *
 * @param params - The inputs.
 * @param params.value - The entry's selection value.
 * @param params.isSelected - Whether this entry is the active selection.
 * @returns The icon element for the entry.
 */
function iconForEntry({
  value,
  isSelected,
}: {
  value: string
  isSelected: boolean
}): React.JSX.Element {
  if (value === FOLDER_ALL) {
    return <Layers className="size-4" aria-hidden="true" />
  }
  if (isSelected) {
    return <FolderOpen className="size-4" aria-hidden="true" />
  }
  return <Folder className="size-4" aria-hidden="true" />
}

/**
 * Collapsible sidebar for the Diagram_Browser.
 *
 * @param props - Component props.
 * @param props.folders - The folder entries to list, in display order.
 * @param props.selectedFolder - The active folder selection.
 * @param props.onSelectFolder - Handler called when an entry is selected.
 * @returns The rendered sidebar element.
 */
export default function DiagramsSidebar({
  folders,
  selectedFolder,
  onSelectFolder,
}: DiagramsSidebarProps): React.JSX.Element {
  /**
   * Render one folder entry as a full-width button in the expanded sidebar,
   * showing its label and count.
   *
   * @param entry - The folder entry to render.
   * @returns The rendered button.
   */
  const renderEntryButton = (entry: FolderEntry): React.JSX.Element => {
    const isSelected: boolean = entry.value === selectedFolder
    return (
      <button
        key={entry.value}
        type="button"
        onClick={() => onSelectFolder(entry.value)}
        aria-label={`${entry.label} (${entry.count})`}
        // aria-current tells screen readers which entry is the active view.
        aria-current={isSelected ? "page" : undefined}
        className={cn(
          "flex items-center justify-between gap-2 text-left text-xs px-2 py-1 rounded-md transition-colors w-full",
          isSelected ? "bg-accent text-accent-foreground font-medium" : "hover:bg-muted",
        )}
      >
        <span className="flex items-center gap-2 min-w-0">
          {iconForEntry({ value: entry.value, isSelected })}
          {/* title shows the full folder path on hover when the label is
              truncated; a native title suits the icon+label+count button row
              better than a nested TruncatedCell (Requirement 6.4). */}
          <span className="truncate" title={entry.label}>
            {entry.label}
          </span>
        </span>
        <span className="shrink-0 text-muted-foreground tabular-nums">{entry.count}</span>
      </button>
    )
  }

  /**
   * Render one folder entry as an icon-only button with a tooltip for the
   * collapsed rail.
   *
   * @param entry - The folder entry to render.
   * @returns The rendered icon button wrapped in a tooltip.
   */
  const renderCollapsedButton = (entry: FolderEntry): React.JSX.Element => {
    const isSelected: boolean = entry.value === selectedFolder
    return (
      <Tooltip key={entry.value}>
        <TooltipTrigger asChild>
          <span className="w-full flex justify-center">
            <button
              type="button"
              onClick={() => onSelectFolder(entry.value)}
              aria-label={`${entry.label} (${entry.count})`}
              aria-current={isSelected ? "page" : undefined}
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded transition-colors",
                isSelected ? "bg-accent text-accent-foreground" : "hover:bg-muted cursor-pointer",
              )}
            >
              {iconForEntry({ value: entry.value, isSelected })}
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="right">
          {entry.label} ({entry.count})
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <SecondarySideBar
      sidebarTitle={SIDEBAR_TITLE}
      collapsedContent={<>{folders.map((entry) => renderCollapsedButton(entry))}</>}
    >
      <div className="flex flex-col gap-0.5 overflow-y-auto">
        {folders.map((entry) => renderEntryButton(entry))}
      </div>
    </SecondarySideBar>
  )
}
